import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import ReactPaginate from 'react-paginate';
import { Link } from 'react-router-dom';
import { FormGroup, Input, Table, Alert, Button, Label, Modal, ModalHeader, ModalBody } from 'reactstrap';

import {
    fetchApiLogs, fetchApiLog, logsRange, statusClassOf, LOGS_PERIODS, STATUS_CLASSES, METHODS,
    MAX_LOGS_HOURS, DEFAULT_LOGS_ENTRIES, MAX_LOGS_ENTRIES,
} from '../helpers/apiLogs';
import { formatNumber, formatMs, formatBytes } from '../helpers/format';
import CopyButton from './copyButton';
import { PayloadCell, ReplicaState, JsonValue } from './externalApiLogDetail';

/**
* Logs tab of the "API analytics (all requests)" page: raw requests from
* the `logs` collection, at most 24 h at a time (the collection is huge).
* Links from the Routes / Who calls us tabs re-seed the filters through
* `initialFilters` + `resetToken`.
*/

const SLOW_MS = 1000;

const styles = {
    filterBar: {
        display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 14,
        backgroundColor: '#FFFFFF', borderRadius: 6, padding: '16px 18px',
        boxShadow: '0 10px 20px rgba(0,0,0,0.06)', marginBottom: 20,
    },
    filterGroup: { marginBottom: 0, minWidth: 120 },
    filterLabel: { fontSize: 11, fontWeight: 600, color: '#818FA5', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 },
    mono: { fontFamily: "'SFMono-Regular', Menlo, Monaco, Consolas, monospace", fontSize: 12 },
    tableCard: { backgroundColor: '#FFFFFF', borderRadius: 6, boxShadow: '0 10px 20px rgba(0,0,0,0.06)', padding: '4px 0 10px 0' },
    footer: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 18px', flexWrap: 'wrap', gap: 10 },
    emptyState: { textAlign: 'center', padding: '50px 20px', color: '#818FA5', fontSize: 14 },
    kvGrid: { display: 'grid', gridTemplateColumns: '120px 1fr', rowGap: 10, columnGap: 12, fontSize: 13, marginBottom: 20 },
    kvKey: { color: '#818FA5', fontWeight: 500 },
    kvValue: { color: '#3A3F56', wordBreak: 'break-word' },
    json: {
        margin: 0, padding: 10, backgroundColor: '#F4F6F8', border: '1px solid #E6EDF5', borderRadius: 4,
        fontFamily: "'SFMono-Regular', Menlo, Monaco, Consolas, monospace", fontSize: 12,
        whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: 420, overflow: 'auto',
    },
    sectionTitle: { fontSize: 12, fontWeight: 700, color: '#818FA5', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 },
    badge: { display: 'inline-block', padding: '1px 7px', borderRadius: 10, fontSize: 11, fontWeight: 600, backgroundColor: '#E6EDF5', color: '#33405E', whiteSpace: 'nowrap' },
    extBadge: { display: 'inline-block', padding: '1px 7px', borderRadius: 10, fontSize: 11, fontWeight: 600, backgroundColor: '#E3EEFB', color: '#1c5cab', whiteSpace: 'nowrap', textDecoration: 'none' },
    tileGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 10, marginBottom: 14 },
    tile: { backgroundColor: '#F4F6F8', borderRadius: 6, padding: '8px 10px' },
    tileLabel: { fontSize: 10, color: '#818FA5', textTransform: 'uppercase', letterSpacing: 0.3 },
    tileValue: { fontSize: 16, fontWeight: 600, color: '#3A3F56', marginTop: 2 },
    requestIdBar: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' },
};

const isNum = (v) => v !== null && v !== undefined && v !== '' && !isNaN(Number(v));

/**
* Link to the same request in the external API analytics (its Logs tab,
* filtered to this request ID, a window around the request's time).
*/
const externalApiLogsUrl = (log) => {
    let qs = new URLSearchParams();
    qs.set('tab', 'logs');
    if (log.requestId) qs.set('logsRequestId', log.requestId);
    if (log.at) {
        qs.set('logsFrom', moment(log.at).subtract(1, 'hour').toISOString());
        qs.set('logsTo', moment(log.at).add(1, 'hour').toISOString());
    }
    return `/api-analytics?${qs.toString()}`;
};

function ExternalApiLink({ log, lang }) {
    return (
        <Link
            to={externalApiLogsUrl(log)}
            style={styles.extBadge}
            title={'Open this request in API analytics (external API)'.translate(lang)}
            onClick={(e) => e.stopPropagation()}
        >
            {'External API'.translate(lang)} ↗
        </Link>
    );
}

export const defaultLogsFilters = {
    preset: '1h',
    customFrom: '',
    customTo: '',
    method: '',
    statusClass: '',
    status: '',
    minMs: '',
    endpoint: '',
    userAgent: '',
    ip: '',
    username: '',
    requestId: '',
    route: '',
    minDbQueries: '',
    externalApi: '',
    replica: '',
    sortField: 'time',
    sortType: -1,
    page: 0,
    entries: DEFAULT_LOGS_ENTRIES,
};

// free-text filters are debounced, the rest apply right away
const TEXT_KEYS = ['endpoint', 'userAgent', 'ip', 'username', 'status', 'minMs', 'requestId', 'route', 'minDbQueries'];

const statusBadgeClass = (status) => {
    let cls = statusClassOf(status);
    if (cls === '2xx') return 'api-key-badge api-key-badge-active';
    if (cls === '3xx') return 'api-key-badge api-key-badge-grace';
    if (cls === '4xx') return 'api-key-badge api-key-badge-disabled';
    if (cls === '5xx') return 'api-key-badge api-key-badge-expired';
    return 'api-key-badge api-key-badge-revoked';
};

const prettyBody = (body) => {
    if (body === null || body === undefined || body === '') return null;
    if (typeof body !== 'string') return JSON.stringify(body, null, 2);
    try {
        return JSON.stringify(JSON.parse(body), null, 2);
    } catch (e) {
        return body;
    }
};

const DETAIL_TABS = [
    { key: 'request', label: 'Request' },
    { key: 'headers', label: 'Headers' },
    { key: 'body', label: 'Body' },
    { key: 'error', label: 'Error' },
    { key: 'db', label: 'Database' },
];

const opKey = (q) => `${q.collection}|${q.op}|${q.filter || q.pipeline || q.update || ''}`;

/**
* Every DB query of the request as a bar from `atMs`, `durationMs` long,
* against the request's own duration - the way a browser's network tab
* draws it. Click a bar or a row to see its filter / pipeline.
*/
function OpsTimeline({ ops, totalMs, lang }) {
    let [selected, setSelected] = useState(null);
    let total = Number(totalMs) || Math.max(1, ...ops.map((q) => (q.atMs || 0) + (q.durationMs || 0)));
    let counts = {};
    ops.forEach((q) => { counts[opKey(q)] = (counts[opKey(q)] || 0) + 1; });

    return (
        <div>
            <div style={{ marginBottom: 12 }}>
                {ops.map((q, idx) => {
                    let unfinished = !isFinite(Number(q.durationMs)) || q.durationMs === null;
                    let left = Math.max(0, Math.min(100, ((q.atMs || 0) / total) * 100));
                    let width = Math.max(0.5, Math.min(100 - left, ((unfinished ? total - (q.atMs || 0) : q.durationMs) / total) * 100));
                    let slow = !unfinished && Number(q.durationMs) > 100;
                    return (
                        <div
                            key={idx}
                            style={{ position: 'relative', height: 18, marginBottom: 3, cursor: 'pointer', outline: selected === idx ? '1px solid #33405E' : 'none', borderRadius: 3 }}
                            title={`#${q.n} ${q.collection}.${q.op} — ${unfinished ? 'unfinished'.translate(lang) : formatMs(q.durationMs)}`}
                            onClick={() => setSelected(selected === idx ? null : idx)}
                        >
                            <div style={{ position: 'absolute', inset: 0, backgroundColor: '#F4F6F8', borderRadius: 3 }} />
                            <div style={{ position: 'absolute', top: 2, height: 14, left: `${left}%`, width: `${width}%`, minWidth: 2, borderRadius: 3, backgroundColor: q.error ? '#d03b3b' : slow ? '#eb6834' : '#2a78d6', border: unfinished ? '1px dashed #818FA5' : 'none' }} />
                        </div>
                    );
                })}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#818FA5' }}>
                    <span>0 ms</span>
                    <span>{formatMs(total)}</span>
                </div>
            </div>

            <Table size="sm" hover responsive style={{ fontSize: 12 }}>
                <thead>
                    <tr>
                        <th>#</th>
                        <th>{'Table'.translate(lang)}</th>
                        <th>{'Op'.translate(lang)}</th>
                        <th>{'Start'.translate(lang)}</th>
                        <th>{'Duration'.translate(lang)}</th>
                        <th>{'Docs'.translate(lang)}</th>
                    </tr>
                </thead>
                <tbody>
                    {ops.map((q, idx) => {
                        let repeated = counts[opKey(q)];
                        let open = selected === idx;
                        return (
                            <React.Fragment key={idx}>
                                <tr style={{ cursor: 'pointer', backgroundColor: open ? '#E6EDF5' : repeated > 1 ? '#FFF4E0' : undefined }} onClick={() => setSelected(open ? null : idx)}>
                                    <td>{q.n}</td>
                                    <td style={styles.mono}>
                                        {q.collection}
                                        {repeated > 1 ? <span style={{ ...styles.badge, marginLeft: 6 }}>{`${'repeats'.translate(lang)} ${repeated}×`}</span> : null}
                                    </td>
                                    <td style={styles.mono}>{q.op}</td>
                                    <td>{isNum(q.atMs) ? `+${formatMs(q.atMs)}` : '—'}</td>
                                    <td style={{ color: Number(q.durationMs) > 100 ? '#d03b3b' : undefined }}>{isNum(q.durationMs) ? formatMs(q.durationMs) : 'unfinished'.translate(lang)}</td>
                                    <td>{isNum(q.docs) ? formatNumber(q.docs) : '—'}</td>
                                </tr>
                                {open ? (
                                    <tr style={{ backgroundColor: '#F4F6F8' }}>
                                        <td colSpan={6}>
                                            <PayloadCell lang={lang} query={q} />
                                            {q.error ? <Alert color="danger" style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>{typeof q.error === 'string' ? q.error : JSON.stringify(q.error)}</Alert> : null}
                                        </td>
                                    </tr>
                                ) : null}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </Table>
        </div>
    );
}

function DbTab({ log, lang }) {
    let db = log.db;

    if (!db) {
        return <span style={{ color: '#818FA5', fontSize: 13 }}>{'No DB data (request logged before detailed logging was deployed).'.translate(lang)}</span>;
    }

    let tile = (label, value) => (
        <div style={styles.tile}>
            <div style={styles.tileLabel}>{label.translate(lang)}</div>
            <div style={styles.tileValue}>{value}</div>
        </div>
    );

    return (
        <div>
            <div style={styles.tileGrid}>
                {tile('Queries', formatNumber(db.queries))}
                {tile('Reads / writes', `${formatNumber(db.reads)} / ${formatNumber(db.writes)}`)}
                {tile('Total DB time', formatMs(db.totalMs))}
                {tile('Slowest query', formatMs(db.slowestMs))}
                {tile('Docs returned', formatNumber(db.docsReturned))}
                {db.unfinished ? tile('Unfinished', formatNumber(db.unfinished)) : null}
            </div>

            {(db.byCollection || []).length ? (
                <Table size="sm" style={{ fontSize: 12, marginBottom: 18 }}>
                    <thead><tr><th>{'Table'.translate(lang)}</th><th>{'Queries'.translate(lang)}</th><th>{'Total ms'.translate(lang)}</th><th>{'Docs'.translate(lang)}</th></tr></thead>
                    <tbody>
                        {db.byCollection.map((c) => (
                            <tr key={c.collection}>
                                <td style={styles.mono}>{c.collection}</td>
                                <td>{formatNumber(c.queries)}</td>
                                <td>{formatMs(c.totalMs)}</td>
                                <td>{formatNumber(c.docs)}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            ) : null}

            <div style={styles.sectionTitle}>{'Query timeline'.translate(lang)}</div>
            {log.ops === null || log.ops === undefined ? (
                <Alert color="info" style={{ fontSize: 13 }}>{'Query details are kept for 7 days.'.translate(lang)}</Alert>
            ) : log.ops.length ? (
                <>
                    {db.opsTruncated ? <Alert color="warning" style={{ fontSize: 13 }}>{`${'Showing'.translate(lang)} ${formatNumber(log.ops.length)} ${'of'.translate(lang)} ${formatNumber(db.queries)} ${'queries'.translate(lang)}.`}</Alert> : null}
                    <OpsTimeline ops={log.ops} totalMs={log.executionTime} lang={lang} />
                </>
            ) : <span style={{ color: '#818FA5', fontSize: 13 }}>{'No queries.'.translate(lang)}</span>}
        </div>
    );
}

function ApiLogDetail({ logId, onClose, lang }) {
    let [log, setLog] = useState(null);
    let [loading, setLoading] = useState(false);
    let [error, setError] = useState(null);
    let [tab, setTab] = useState('request');

    useEffect(() => {
        if (!logId) {
            setLog(null);
            setError(null);
            return;
        }

        setLoading(true);
        setError(null);
        setTab('request');
        fetchApiLog(logId).then(({ data, error: err }) => {
            setLoading(false);
            if (err) setError(err);
            else setLog(data);
        });
    }, [logId]);

    let body = log ? prettyBody(log.body) : null;
    let hasError = log && log.error !== null && log.error !== undefined && log.error !== '';
    let kv = (label, value, mono) => (
        <>
            <div style={styles.kvKey}>{label.translate(lang)}</div>
            <div style={{ ...styles.kvValue, ...(mono ? styles.mono : null) }}>{value === null || value === undefined || value === '' ? '—' : value}</div>
        </>
    );
    let headers = log && log.headers && typeof log.headers === 'object' ? Object.keys(log.headers).sort() : [];

    return (
        <Modal isOpen={!!logId} toggle={onClose} size="lg" scrollable className="log-detail-dialog">
            <ModalHeader toggle={onClose}>{'Request log'.translate(lang)}</ModalHeader>
            <ModalBody>
                {loading ? <div style={{ color: '#818FA5' }}>{'Loading...'.translate(lang)}</div> : null}
                {error ? <Alert color="danger">{error}</Alert> : null}
                {!loading && log ? (
                    <>
                        <div className="table-tabs" style={{ marginBottom: 16 }}>
                            {DETAIL_TABS.filter((tb) => tb.key !== 'error' || hasError).map((tb) => (
                                <button key={tb.key} className={tab === tb.key ? 'active' : ''} onClick={() => setTab(tb.key)}>
                                    {tb.label.translate(lang)}
                                    {tb.key === 'db' && log.db ? ` (${formatNumber(log.db.queries)})` : ''}
                                </button>
                            ))}
                        </div>

                        {tab === 'request' ? (
                            <>
                                <div style={styles.kvGrid}>
                                    {kv('Time', log.at ? moment(log.at).format('YYYY-MM-DD HH:mm:ss.SSS') : null)}
                                    {kv('Request ID', log.requestId ? (
                                        <>
                                            {log.requestId}
                                            {log.requestIdSource ? <span style={{ ...styles.badge, marginLeft: 6 }}>{log.requestIdSource}</span> : null}
                                            <CopyButton lang={lang} value={log.requestId} label="Copy request ID" />
                                        </>
                                    ) : null, true)}
                                    {kv('Method', log.method, true)}
                                    {kv('Endpoint', log.endpoint, true)}
                                    {kv('Route', log.route, true)}
                                    {kv('Status', <span className={statusBadgeClass(log.status)}>{log.status}</span>)}
                                    {kv('Duration', formatMs(log.executionTime))}
                                    {kv('Response size', isNum(log.responseBytes) ? formatBytes(log.responseBytes) : null)}
                                    {kv('External API', log.externalApi ? <ExternalApiLink log={log} lang={lang} /> : 'No'.translate(lang))}
                                    {kv('User', log.username)}
                                    {kv('UID', log.uid, true)}
                                    {kv('IP', log.ip, true)}
                                    {kv('Client IP', log.clientIp, true)}
                                    {kv('Forwarded for', log.forwardedFor, true)}
                                    {kv('User agent', log.userAgent)}
                                    {kv('Referer', log.referer)}
                                    {kv('Body size', isNum(log.bodySize) ? formatBytes(log.bodySize) : null)}
                                    {kv('Body hash', log.bodyHash, true)}
                                    {kv('Fingerprint', log.fingerprint, true)}
                                </div>
                                <ReplicaState log={{ ...log, durationMs: log.executionTime }} lang={lang} />
                            </>
                        ) : null}

                        {tab === 'headers' ? (
                            headers.length ? (
                                <Table size="sm" style={{ fontSize: 12 }}>
                                    <thead><tr><th>{'Name'.translate(lang)}</th><th>{'Value'.translate(lang)}</th></tr></thead>
                                    <tbody>
                                        {headers.map((name) => (
                                            <tr key={name}>
                                                <td style={{ ...styles.mono, whiteSpace: 'nowrap' }}>{name}</td>
                                                <td style={{ ...styles.mono, wordBreak: 'break-all' }}>{typeof log.headers[name] === 'object' ? JSON.stringify(log.headers[name]) : String(log.headers[name])}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            ) : <span style={{ color: '#818FA5', fontSize: 13 }}>—</span>
                        ) : null}

                        {tab === 'body' ? (
                            <>
                                <div style={styles.sectionTitle}>
                                    {'Body'.translate(lang)}
                                    {body ? <CopyButton lang={lang} value={typeof log.body === 'string' ? log.body : body} label="Copy body" /> : null}
                                </div>
                                {body ? <pre style={styles.json}>{body}</pre> : <span style={{ color: '#818FA5', fontSize: 13 }}>—</span>}
                                <div style={{ fontSize: 11, color: '#818FA5', margin: '4px 0 16px 0' }}>{'Passwords, tokens and keys are masked (***); the body is cut at 10,000 characters.'.translate(lang)}</div>
                                <div style={styles.sectionTitle}>{'Query'.translate(lang)}</div>
                                <div style={{ marginBottom: 16 }}><JsonValue value={log.query} empty="—" /></div>
                                <div style={styles.sectionTitle}>{'Params'.translate(lang)}</div>
                                <JsonValue value={log.params} empty="—" />
                            </>
                        ) : null}

                        {tab === 'error' && hasError ? <pre style={styles.json}>{prettyBody(log.error)}</pre> : null}

                        {tab === 'db' ? <DbTab log={log} lang={lang} /> : null}
                    </>
                ) : null}
            </ModalBody>
        </Modal>
    );
}

/**
* @param {object} [initialFilters] - applied on mount and whenever `resetToken` changes
* @param {*} [resetToken] - bump to re-apply `initialFilters` (a link from another tab)
* @param {Array} [replicaSlots] - replica slots from the overview, for the replica dropdown (a number field without them)
*/
function ApiLogsList({ lang, initialFilters, resetToken, replicaSlots }) {
    let [filters, setFilters] = useState({ ...defaultLogsFilters, ...(initialFilters || {}) });
    let [drafts, setDrafts] = useState(() => pickText({ ...defaultLogsFilters, ...(initialFilters || {}) }));
    let [items, setItems] = useState([]);
    let [total, setTotal] = useState(0);
    let [loading, setLoading] = useState(true);
    let [error, setError] = useState(null);
    let [rangeError, setRangeError] = useState(false);
    let [openLogId, setOpenLogId] = useState(null);
    // "now" for the quick presets, fixed until the preset changes or Refresh is clicked
    let [now, setNow] = useState(() => Date.now());
    let timer = useRef(null);
    let lastResetToken = useRef(resetToken);

    let update = (patch) => setFilters((prev) => ({ ...prev, ...patch }));

    useEffect(() => {
        if (resetToken === undefined || resetToken === lastResetToken.current) return;
        lastResetToken.current = resetToken;
        let next = { ...defaultLogsFilters, ...(initialFilters || {}) };
        setFilters(next);
        setDrafts(pickText(next));
        setNow(Date.now());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resetToken]);

    useEffect(() => {
        if (TEXT_KEYS.every((key) => drafts[key] === filters[key])) return;
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => update({ ...drafts, page: 0 }), 350);
        return () => { if (timer.current) clearTimeout(timer.current); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [drafts]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    let range = React.useMemo(() => logsRange(filters), [filters.preset, filters.customFrom, filters.customTo, now]);
    let rangeTooLong = !!range && range.hours > MAX_LOGS_HOURS;
    let requestKey = JSON.stringify({ ...filters, range });

    useEffect(() => {
        if (!range || rangeTooLong) {
            setLoading(false);
            setItems([]);
            setTotal(0);
            return;
        }

        setLoading(true);
        setError(null);
        setRangeError(false);

        fetchApiLogs({
            from: range.from,
            to: range.to,
            method: filters.method,
            status: filters.status,
            statusClass: !filters.status ? filters.statusClass : '',
            minMs: filters.minMs,
            endpoint: filters.endpoint,
            userAgent: filters.userAgent,
            ip: filters.ip,
            username: filters.username,
            requestId: filters.requestId.trim(),
            route: filters.route.trim(),
            minDbQueries: filters.minDbQueries,
            externalApi: filters.externalApi,
            replica: filters.replica,
            sortField: filters.sortField,
            sortType: filters.sortType,
            page: filters.page,
            entries: filters.entries,
        }).then(({ items: list, total: count, error: err, rangeTooLong: tooLong }) => {
            setLoading(false);
            setItems(list);
            setTotal(count);
            setRangeError(tooLong);
            setError(tooLong ? null : err);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestKey]);

    let sortBy = (field) => update({ sortField: field, sortType: filters.sortField === field && Number(filters.sortType) === -1 ? 1 : -1, page: 0 });
    let sortMark = (field) => (filters.sortField === field ? (Number(filters.sortType) === 1 ? '▲' : '▼') : '');
    let totalPages = Math.max(1, Math.ceil(total / (Number(filters.entries) || DEFAULT_LOGS_ENTRIES)));
    let textInput = (key, label, props = {}) => (
        <FormGroup style={styles.filterGroup}>
            <div style={styles.filterLabel}>{label.translate(lang)}</div>
            <Input style={{ height: 42 }} value={drafts[key]} onChange={(e) => setDrafts({ ...drafts, [key]: e.target.value })} {...props} />
        </FormGroup>
    );

    return (
        <div>
            <div style={styles.filterBar}>
                <FormGroup style={styles.filterGroup}>
                    <div style={styles.filterLabel}>{'Period'.translate(lang)}</div>
                    <div className="table-tabs" style={{ marginBottom: 0 }}>
                        {LOGS_PERIODS.map((p) => (
                            <button key={p.value} className={filters.preset === p.value ? 'active' : ''} onClick={() => { update({ preset: p.value, page: 0 }); setNow(Date.now()); }}>
                                {p.label.translate(lang)}
                            </button>
                        ))}
                    </div>
                </FormGroup>

                {filters.preset === 'custom' ? (
                    <>
                        <FormGroup style={styles.filterGroup}>
                            <div style={styles.filterLabel}>{'From'.translate(lang)}</div>
                            <Input type="datetime-local" style={{ height: 42 }} value={filters.customFrom} onChange={(e) => update({ customFrom: e.target.value, page: 0 })} />
                        </FormGroup>
                        <FormGroup style={styles.filterGroup}>
                            <div style={styles.filterLabel}>{'To'.translate(lang)}</div>
                            <Input type="datetime-local" style={{ height: 42 }} value={filters.customTo} onChange={(e) => update({ customTo: e.target.value, page: 0 })} />
                        </FormGroup>
                    </>
                ) : null}

                <FormGroup style={styles.filterGroup}>
                    <div style={styles.filterLabel}>{'Method'.translate(lang)}</div>
                    <Input type="select" style={{ height: 42 }} value={filters.method} onChange={(e) => update({ method: e.target.value, page: 0 })}>
                        <option value="">{'All'.translate(lang)}</option>
                        {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                    </Input>
                </FormGroup>

                <FormGroup style={styles.filterGroup}>
                    <div style={styles.filterLabel}>{'Status class'.translate(lang)}</div>
                    <Input type="select" style={{ height: 42 }} value={filters.statusClass} disabled={!!filters.status} onChange={(e) => update({ statusClass: e.target.value, page: 0 })}>
                        <option value="">{'All'.translate(lang)}</option>
                        {STATUS_CLASSES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </Input>
                </FormGroup>

                {textInput('status', 'Exact status', { type: 'number', style: { height: 42, maxWidth: 110 } })}
                {textInput('minMs', 'Min ms', { type: 'number', style: { height: 42, maxWidth: 110 } })}
                {textInput('endpoint', 'Endpoint contains', { placeholder: '/products/' })}
                {textInput('username', 'User')}
                {textInput('ip', 'IP')}
                {textInput('userAgent', 'User agent contains')}
                {textInput('route', 'Route (exact)', { placeholder: 'GET /products/:param', title: 'Exact route template, only logs written after the deploy have it' })}
                {textInput('minDbQueries', 'Min DB queries', { type: 'number', style: { height: 42, maxWidth: 110 } })}

                <FormGroup style={styles.filterGroup}>
                    <div style={styles.filterLabel}>{'Replica'.translate(lang)}</div>
                    {(replicaSlots || []).length ? (
                        <Input type="select" style={{ height: 42 }} value={filters.replica} onChange={(e) => update({ replica: e.target.value, page: 0 })}>
                            <option value="">{'All'.translate(lang)}</option>
                            {Array.from(new Set([...replicaSlots.map(String), ...(filters.replica ? [String(filters.replica)] : [])]))
                                .sort((a, b) => Number(a) - Number(b))
                                .map((r) => <option key={r} value={r}>{r}</option>)}
                        </Input>
                    ) : (
                        <Input type="number" min={1} style={{ height: 42, maxWidth: 100 }} placeholder={'All'.translate(lang)} value={filters.replica} onChange={(e) => update({ replica: e.target.value, page: 0 })} />
                    )}
                </FormGroup>

                <FormGroup style={styles.filterGroup}>
                    <div style={styles.filterLabel}>{'External API'.translate(lang)}</div>
                    <Input type="select" style={{ height: 42 }} value={filters.externalApi} onChange={(e) => update({ externalApi: e.target.value, page: 0 })}>
                        <option value="">{'All'.translate(lang)}</option>
                        <option value="true">{'External API only'.translate(lang)}</option>
                        <option value="false">{'Without external API'.translate(lang)}</option>
                    </Input>
                </FormGroup>

                <Button color="secondary" outline style={{ height: 42 }} onClick={() => setNow(Date.now())}>{'Refresh'.translate(lang)}</Button>
                <Button
                    color="secondary"
                    outline
                    style={{ height: 42 }}
                    onClick={() => { setFilters({ ...defaultLogsFilters, preset: filters.preset }); setDrafts(pickText(defaultLogsFilters)); }}
                >
                    {'Reset filters'.translate(lang)}
                </Button>
            </div>

            {!range ? <Alert color="info">{'Pick both a from and a to time for a custom period.'.translate(lang)}</Alert> : null}
            {rangeTooLong || rangeError ? (
                <Alert color="warning">{`${'Logs can be viewed for at most'.translate(lang)} ${MAX_LOGS_HOURS} h ${'at a time. For a longer period, use the Overview tab.'.translate(lang)}`}</Alert>
            ) : null}
            {error ? <Alert color="danger">{error}</Alert> : null}

            <div style={styles.requestIdBar}>
                <Input
                    type="text"
                    style={{ height: 40, maxWidth: 380, ...styles.mono }}
                    placeholder={'Request ID (from the X-Request-ID header)...'.translate(lang)}
                    value={drafts.requestId}
                    onChange={(e) => setDrafts({ ...drafts, requestId: e.target.value })}
                />
                {filters.requestId ? <span style={{ fontSize: 12, color: '#818FA5' }}>{'Searching only this request ID, within the selected period.'.translate(lang)}</span> : null}
            </div>

            <div style={styles.tableCard}>
                <Table responsive hover>
                    <thead>
                        <tr>
                            <th style={{ cursor: 'pointer' }} onClick={() => sortBy('time')}>{'Time'.translate(lang)} {sortMark('time')}</th>
                            <th>{'Method'.translate(lang)}</th>
                            <th>{'Endpoint'.translate(lang)}</th>
                            <th style={{ cursor: 'pointer' }} onClick={() => sortBy('status')}>{'Status'.translate(lang)} {sortMark('status')}</th>
                            <th style={{ cursor: 'pointer' }} onClick={() => sortBy('executionTime')}>{'Duration'.translate(lang)} {sortMark('executionTime')}</th>
                            <th style={{ cursor: 'pointer' }} onClick={() => sortBy('dbQueries')}>{'Queries'.translate(lang)} {sortMark('dbQueries')}</th>
                            <th style={{ cursor: 'pointer' }} onClick={() => sortBy('dbTimeMs')}>{'DB ms'.translate(lang)} {sortMark('dbTimeMs')}</th>
                            <th style={{ cursor: 'pointer' }} onClick={() => sortBy('responseBytes')}>{'Response'.translate(lang)} {sortMark('responseBytes')}</th>
                            <th>{'User'.translate(lang)}</th>
                            <th>{'IP'.translate(lang)}</th>
                            <th>{'Replica'.translate(lang)}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={11} style={styles.emptyState}>{'Loading...'.translate(lang)}</td></tr>
                        ) : !items.length ? (
                            <tr><td colSpan={11} style={styles.emptyState}>{'No requests in the selected period'.translate(lang)}</td></tr>
                        ) : items.map((log) => {
                            let slow = Number(log.executionTime) >= SLOW_MS;
                            return (
                                <tr key={log._id} style={{ cursor: 'pointer' }} onClick={() => setOpenLogId(log._id)}>
                                    <td style={{ whiteSpace: 'nowrap' }}>{log.at ? moment(log.at).format('YYYY-MM-DD HH:mm:ss') : '-'}</td>
                                    <td style={styles.mono}>{log.method}</td>
                                    <td style={{ ...styles.mono, maxWidth: 420, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={log.endpoint}>
                                        {log.externalApi ? <><ExternalApiLink log={log} lang={lang} />{' '}</> : null}
                                        {log.endpoint}
                                    </td>
                                    <td><span className={statusBadgeClass(log.status)}>{log.status}</span></td>
                                    <td style={{ whiteSpace: 'nowrap', color: slow ? '#d03b3b' : undefined, fontWeight: slow ? 600 : 400 }}>{formatMs(log.executionTime)}</td>
                                    <td>{log.db && isNum(log.db.queries) ? formatNumber(log.db.queries) : '—'}</td>
                                    <td style={{ whiteSpace: 'nowrap' }}>{log.db && isNum(log.db.totalMs) ? formatMs(log.db.totalMs) : '—'}</td>
                                    <td style={{ whiteSpace: 'nowrap' }}>{isNum(log.responseBytes) ? formatBytes(log.responseBytes) : '—'}</td>
                                    <td>{log.username || '—'}</td>
                                    <td style={styles.mono}>{log.ip || '—'}</td>
                                    <td>{isNum(log.replica) ? log.replica : '—'}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>

                <div style={styles.footer}>
                    <span style={{ fontSize: 13, color: '#818FA5' }}>{`${'Total'.translate(lang)}: ${formatNumber(total)}`}</span>
                    <Label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#818FA5' }}>
                        {'Per page'.translate(lang)}
                        <Input
                            type="select"
                            style={{ minWidth: 70, height: 36 }}
                            value={filters.entries}
                            onChange={(e) => update({ entries: Math.min(MAX_LOGS_ENTRIES, Number(e.target.value)), page: 0 })}
                        >
                            {[25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
                        </Input>
                    </Label>
                    <ReactPaginate
                        previousLabel={'<'}
                        nextLabel={'>'}
                        breakLabel={'...'}
                        pageCount={totalPages}
                        marginPagesDisplayed={1}
                        pageRangeDisplayed={2}
                        onPageChange={(p) => update({ page: p.selected })}
                        containerClassName={'pagination'}
                        pageClassName={'page-item'}
                        pageLinkClassName={'page-link'}
                        previousClassName={'page-item-next'}
                        previousLinkClassName={'page-link'}
                        nextClassName={'page-item-next'}
                        nextLinkClassName={'page-link'}
                        activeClassName={'active'}
                        forcePage={Math.max(0, Number(filters.page) || 0)}
                    />
                </div>
            </div>

            <ApiLogDetail logId={openLogId} onClose={() => setOpenLogId(null)} lang={lang} />
        </div>
    );
}

function pickText(filters) {
    let out = {};
    TEXT_KEYS.forEach((key) => { out[key] = filters[key] || ''; });
    return out;
}

export default ApiLogsList;
